import React, { useEffect, useState, useMemo } from "react";
import { useOutletContext, useNavigate, Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Flame, ArrowRight, Compass, Users, Sparkles } from "lucide-react";
import MirrorCard from "@/components/MirrorCard";
import SoulCompassBadge from "@/components/SoulCompassBadge";
import PageHero from "@/components/PageHero";
import N8Emblem from "@/components/N8Emblem";
import SeasonalNote from "@/components/SeasonalNote";
import HomeN8Guide from "@/components/HomeN8Guide";
import TodaysIntentionCard from "@/components/TodaysIntentionCard";
import PinnedAffirmationCard from "@/components/PinnedAffirmationCard";
import { HERO } from "@/lib/heroImages";

/**
 * Home — the daily starting point. One calm, coherent surface answering
 * the single question:
 *
 *   "What can I do today that moves me closer to the person I'm becoming?"
 *
 * Six slots, in order:
 *   1. Greeting              — "Good morning, {name}"
 *   2. Core reflection       — one North-Star line, always the same shape
 *   3. Focus today           — ONE personalised action (from N8 Guide priorities)
 *   4. A small nudge         — rotates between Soul Moment / Compass Nudge / Sunday Recap / Affirmation
 *   5. Progress glance       — one meaningful stat (intentional streak) — nothing else
 *   6. Connection preview    — one short glance into the community
 *
 * All other historical Home surfaces (Future You breath pacer, Daily
 * Intentional Plan cards, multi-item Today's Intentions checklist, Pinned
 * Affirmation, Principle of the Day, Compass Pattern Nudge, Sunday Recap,
 * Monthly Community Insight) still live in the app — they've been relocated
 * to Rituals / Becoming / Connection so Home stays calm.
 */

function timeOfDayGreeting(now = new Date()) {
  const h = now.getHours();
  if (h >= 5 && h < 12)  return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "A quiet night";
}

export default function Home() {
  const outletCtx = useOutletContext() || {};
  const pulse = outletCtx.pulse || 0;
  const [home, setHome] = useState(null);
  const [guide, setGuide] = useState(null);
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [h, g, p] = await Promise.all([
        endpoints.home().catch(() => ({ data: null })),
        endpoints.n8GuideToday().catch(() => ({ data: null })),
        endpoints.posts("feed").catch(() => ({ data: [] })),
      ]);
      setHome(h.data);
      setGuide(g.data);
      setPosts(Array.isArray(p.data) ? p.data.slice(0, 2) : []);
    })();
  }, [pulse]);

  const displayName = useMemo(
    () => (home?.profile?.display_name || home?.profile?.name || "").trim(),
    [home]
  );
  const greeting = useMemo(() => timeOfDayGreeting(new Date()), []);
  const hasCompass = !!(home?.profile?.soul_compass?.primary_archetype || home?.profile?.soul_compass?.archetype_slug);
  const priorities = guide?.today_priorities || [];
  const focus = priorities[0] || null;

  if (!home) {
    return (
      <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="home-loading">
        Steeping…
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="home-page">
      {/* Cinematic banner header — soft misty ridge at first light */}
      <PageHero
        eyebrow="Today"
        title={
          <>
            {greeting}
            {displayName ? <>, <span style={{ color: "#E19271" }}>{displayName}</span></> : ""}.
          </>
        }
        supporting="Did today's choices move you closer to the person you're becoming?"
        image={HERO.home}
        testid="home-hero"
      />

      {/* TODAY'S INTENTION — the single steady thought carried from Mind →
          Daily Intentions. Sits directly under the hero as a quiet anchor,
          renders nothing when no intention has been set. The simplified
          N8ONE emblem lives INSIDE `TodaysIntentionCard`, so when the
          card returns null the emblem disappears with it — Home never
          floats a lone emblem over empty space. The breathing Guide
          emblem lives on `HomeN8Guide` further down the page. */}
      <TodaysIntentionCard />

      {/* YOUR PINNED AFFIRMATION — surfaces whatever the member intentionally
          pinned from Rituals → Daily Affirmations. Uses the same localStorage
          source of truth; renders nothing when nothing is pinned, so the
          page stays calm for passive users. */}
      <PinnedAffirmationCard />

      {/* A quiet seasonal whisper — only appears on solstices, equinoxes,
          and the first meteorological day of each season. */}
      <SeasonalNote />

      {/* Compass identity chip — quiet, aligned right when present */}
      {hasCompass && (
        <div className="reveal" data-testid="home-compass-chip">
          <SoulCompassBadge profile={home.profile} />
        </div>
      )}

      {/* If the user has not started their Compass, invite them warmly. This
          is the single moment on Home where we ask them to open Becoming. */}
      {!hasCompass && (
        <CompassInvite onOpen={() => navigate("/becoming?section=discover")} />
      )}

      {/* N8 GUIDE — direction. Personal orientation + today's priorities.
          Reads the same guide payload; no duplicate system. */}
      <HomeN8Guide guide={guide} greeting={greeting} />

      {/* THE MIRROR — reflection. A private space to talk it through. */}
      <MirrorCard />

      {/* PROGRESS GLANCE — a concise glimpse into your rhythm. */}
      <ProgressGlance streak={home.streak} />

      {/* CONNECTION PREVIEW — a quiet peek into the Collective. */}
      <ConnectionPreview posts={posts} onOpen={() => navigate("/community")} />
    </div>
  );
}

/* ──────────────────────────── Slot components ──────────────────────────── */

function CompassInvite({ onOpen }) {
  return (
    <section
      className="rounded-lg border hairline bg-gradient-to-br from-[hsl(var(--sand))]/60 to-[hsl(var(--paper))] p-8 md:p-10 reveal reveal-1"
      data-testid="home-compass-invite"
    >
      <div className="flex items-start gap-5 flex-wrap">
        <div
          aria-hidden
          className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]"
        >
          <Compass size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
            Begin the journey
          </div>
          <h2
            className="font-display text-2xl md:text-3xl mt-1 leading-tight"
            data-testid="home-compass-invite-title"
          >
            Start with your Soul Compass.
          </h2>
          <p className="mt-2 max-w-xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            A quiet forty-question reflection that reveals who you are, so every ritual,
            reflection, and recommendation across N8One becomes truly yours.
          </p>
          <button
            type="button"
            onClick={onOpen}
            data-testid="home-compass-invite-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] text-sm hover:bg-[hsl(var(--terracotta))]/90 tab-link"
          >
            Open the Compass <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FocusToday({ focus, guide, onPractice }) {
  if (!focus) {
    return (
      <section
        className="rounded-lg border hairline bg-[hsl(var(--paper))] p-8 md:p-10 reveal reveal-2"
        data-testid="home-focus-empty"
      >
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
          Your focus today
        </div>
        <p className="mt-3 font-display text-2xl leading-snug italic text-[hsl(var(--fg))]/80">
          N8 Guide is listening — check in below or open Rituals to begin.
        </p>
      </section>
    );
  }

  // Focus body comes from the priority's `note` (its actual detail). The
  // "Why this" line is reserved for an explicit reason (`focus.why` /
  // `focus.reason`) — we do NOT fall back to the monthly insight here,
  // because that same insight is the source of the Small Nudge below and
  // duplicating it under two headings on Home reads as noise.
  const why = focus.why || focus.reason || "";
  const title = focus.title || focus.action || "One intentional choice";
  const body = focus.note || focus.action || focus.body || "";

  return (
    <section
      className="relative overflow-hidden rounded-lg border hairline bg-gradient-to-br from-[hsl(var(--terracotta))]/8 via-[hsl(var(--paper))] to-[hsl(var(--sand))]/30 p-8 md:p-10 reveal reveal-2"
      data-testid="home-focus-today"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-[0.08]"
        style={{ background: "radial-gradient(circle, hsl(var(--terracotta)) 0, transparent 65%)" }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
          <Sparkles size={12} />
          <span data-testid="home-focus-eyebrow">Your focus today</span>
        </div>
        <h2
          className="font-display text-3xl md:text-4xl mt-3 leading-[1.1] tracking-tight"
          data-testid="home-focus-title"
        >
          {title}
        </h2>
        {body && (
          <p
            className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[hsl(var(--fg))]/85"
            data-testid="home-focus-body"
          >
            {body}
          </p>
        )}
        {why && (
          <p
            className="mt-4 text-[13px] italic text-[hsl(var(--muted-foreground))] max-w-2xl"
            data-testid="home-focus-why"
          >
            Why this: {why}
          </p>
        )}
        <button
          type="button"
          onClick={onPractice}
          data-testid="home-focus-cta"
          className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[hsl(var(--fg))] text-[hsl(var(--paper))] text-sm hover:opacity-90 tab-link"
        >
          Open in Rituals <ArrowRight size={14} />
        </button>
      </div>
    </section>
  );
}

function SmallNudge({ guide, showing }) {
  // Never repeat the same line the focus card already used as its "Why this".
  // Prefer a distinctly-different source in this order: whisper → affirmation
  // → soul_moment. Fall back to monthly_insight only when nothing else is
  // available AND the focus card is NOT already showing it.
  const primary = guide?.whisper || guide?.affirmation || guide?.soul_moment || "";
  const insight = primary || (showing ? "" : (guide?.monthly_insight || ""));
  if (!insight) return null;
  return (
    <section
      className="rounded-md border hairline bg-[hsl(var(--sand))]/40 p-6 md:p-7 reveal reveal-3"
      data-testid="home-nudge"
    >
      <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))]">
        A small nudge
      </div>
      <p
        className="mt-3 text-base md:text-lg leading-relaxed text-[hsl(var(--fg))]/85 max-w-2xl tabular-nums"
        data-testid="home-nudge-text"
      >
        &ldquo;{insight}&rdquo;
      </p>
    </section>
  );
}

function ProgressGlance({ streak }) {
  return (
    <section
      className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-7 reveal reveal-3"
      data-testid="home-progress"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            Progress
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <Flame size={16} className="text-[hsl(var(--terracotta))]" />
            <span
              className="text-4xl md:text-5xl leading-none tabular-nums font-medium"
              data-testid="home-progress-streak"
            >
              {streak || 0}
            </span>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">
              {streak === 1 ? "day of showing up" : "days of showing up"}
            </span>
          </div>
        </div>
        <Link
          to="/becoming?section=choose"
          data-testid="home-progress-link"
          className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] inline-flex items-center gap-1 tab-link"
        >
          See what you&rsquo;re building <ArrowRight size={11} />
        </Link>
      </div>
    </section>
  );
}

function ConnectionPreview({ posts, onOpen }) {
  if (!posts || posts.length === 0) return null;
  return (
    <section
      className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-7 reveal reveal-4"
      data-testid="home-connection-preview"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--moss))]">
          <Users size={12} />
          <span>The Collective</span>
        </div>
        <button
          type="button"
          onClick={onOpen}
          data-testid="home-connection-cta"
          className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] inline-flex items-center gap-1 tab-link"
        >
          Join the room <ArrowRight size={11} />
        </button>
      </div>
      <ul className="mt-4 space-y-4">
        {posts.map((p) => (
          <li
            key={p.id}
            className="border-t hairline pt-4 first:border-t-0 first:pt-0"
            data-testid={`home-connection-post-${p.id}`}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
              {p.author || "Someone"}
              {p.author_username && (
                <span className="text-[hsl(var(--clay))] normal-case tracking-normal ml-1">
                  · @{p.author_username}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-sm text-[hsl(var(--fg))]/85 leading-relaxed line-clamp-2">
              {p.content}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
